import { matchPath, useLocation } from 'react-router-dom';
import { PATH } from '../router/PATH';
import { isOrganisationOnboarded } from '../router/onboardingGateDecision';
import type { Organization } from '@/api/models';
import { useOrganizationQuery } from '@/api/queries/organizations';
import { canNavigateBack, hasRefreshableSession } from '@/utils';
import { getNavigationBackTarget } from './navigation';

/** Route patterns rendered without the shell. `end: false` is a prefix match. */
const FULL_SCREEN_PATTERNS = [
    { path: PATH.LOGIN, end: true },
    // Every wizard step lives under it, so the whole wizard is full-screen.
    { path: PATH.ONBOARDING, end: false },
    { path: PATH.SETUP_PENDING, end: true },
];

/**
 * Whether a path renders without the sidebar and navbar: the login page, the
 * onboarding wizard and the setup-in-progress page. None of them has a shell
 * to navigate — before sign-in, and before onboarding finishes, there is
 * nothing else the user can open.
 */
export const isFullScreenPath = (pathname: string): boolean =>
    FULL_SCREEN_PATTERNS.some(
        (pattern) => matchPath(pattern, pathname) !== null,
    );

type ShellInput = {
    pathname: string;
    signedIn: boolean;
    /** The organisation once read; `undefined` while loading or failed. */
    organisation: Pick<Organization, 'onboardedAt'> | undefined;
};

/**
 * Whether the shell wraps the current route. Never on a full-screen path.
 * Signed in, only once the organisation is known to be onboarded: before
 * that the onboarding gate is showing its skeleton, its error or a redirect,
 * and a sidebar would flash links to screens the user cannot open yet.
 */
export const shouldWrapWithShell = ({
    pathname,
    signedIn,
    organisation,
}: ShellInput): boolean =>
    !isFullScreenPath(pathname) &&
    (!signedIn ||
        (organisation !== undefined && isOrganisationOnboarded(organisation)));

export const useLayout = () => {
    const location = useLocation();
    const signedIn = hasRefreshableSession();
    // The gate's read: same key, so one request serves both.
    const organisation = useOrganizationQuery(signedIn).data;

    const shouldWrapWithLayout = shouldWrapWithShell({
        pathname: location.pathname,
        signedIn,
        organisation,
    });

    // Pages with a fixed back target (e.g. manual import → /purchases)
    // always offer the back button, even when opened directly with no
    // in-app history to pop.
    const showBack =
        getNavigationBackTarget(location.pathname) !== null ||
        canNavigateBack();

    return {
        showBack,
        shouldWrapWithLayout,
    };
};
